import { isPlatformBrowser, AsyncPipe, Location, LocationStrategy, PathLocationStrategy } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  DOCUMENT,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  viewChild,
  ɵglobal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { catchError, concatMap, delay, filter, map, merge, mergeMap, Observable, of, take, withLatestFrom } from 'rxjs';
import { EnvironmentService } from '../../environments/environment.service';
import { AuthLoginState, LoginType, Token } from '../core/auth/auth.models';
import { selectAuthLoginState } from '../core/auth/auth.selectors';
import { selectSessionID } from '../core/session/session.selectors';
import { LogService } from '../core/log/log.service';
import { UserDTO } from '../domain/user/user.model';
import { selectUser, selectUserId } from '../domain/user/user.selectors';
import { selectAllTopicList, selectCurrentTopic, selectSpecializzazioni, selectCategorie } from '../domain/topic/topic.selector';
import { selectWidgetHomeHeaderBannerContent } from '../domain/widget/widget.selectors';
import { loadHomeHeaderBanner } from '../domain/widget/widget.actions';
import { Content } from '../domain/content/content.model';
import { Topic } from '../domain/topic/topic.model';
import { PanelItem } from '../shared/sidenav-panel/sidenav-panel.component';
import { ActivatedRoute, NavigationEnd, ParamMap, Router, RouterOutlet } from '@angular/router';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { MATERIAL_ANIMATIONS } from '@angular/material/core';
import { FooterComponent } from '../shared/footer/footer.component';
import { ToolbarComponent } from '../shared/toolbar/toolbar.component';
import { SidenavComponent } from '../shared/sidenav/sidenav.component';
import { UserMenuPanelComponent } from '../shared/user-menu-panel/user-menu-panel.component';
import { ConsentManagementComponent } from '../shared/consent-management/consent-management.component';
import { LocalStorageService } from '../core/local-storage/local-storage.service';
import { TranslateService } from '@ngx-translate/core';
import { evaluateBooleanFromString, getQueryParametersFromURL, stripQueryParameters } from '../shared/util/util';
import { activateUser, loadUserGuest } from '../domain/user/user.actions';
import { authRenewToken, authSoftLogin, setLocalStorageAuth } from '../core/auth/auth.actions';
import { onlyWhenIdentityResolved, onlyWhenOnline } from '../shared/util/operators';
import { createSession } from '../core/session/session.actions';
import { ExternalConsent } from '../domain/external-consent/external-consent.model';
import { ExternalConsentActions } from '../domain/external-consent/external-consent.actions';
import { dnLivreaConfig } from '../core/init/init.actions';
import { loadTopic } from '../domain/topic/topic.actions';
import { loadTemplateCt } from '../domain/template-ct/template-ct.actions';
import { GA4ConfigService } from '../shared/gtag/ga4-dual-tracking';
import { selectGetAccessToken } from '../core/auth/auth.selectors';
import { selectCurrentPage } from '../core/current-page/current-page.selectors';
import { ClickLog, ClickOperation, CurrentPage } from '../core/clicks/click.models';
import { ClickService } from '../core/clicks/click.service';

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];

const CONSUMED_QUERY_PARAMS = [
  'tkcm', 'tksl',
  'tag', 'cnt', 'num',
  'flg1', 'flg2', 'flg3', 'flg4',
  ...UTM_PARAMS,
  'callbackUrl', 'clientId', 'scopes',
  'returnTo',
];

@Component({
  selector: 'dottnet-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [
    Location,
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    // Disable Material's slide/fade animation so the sidenav just appears under the toolbar
    // on open. This is the framework switch (MatDrawer skips the .mat-drawer-transition class
    // and simulates its open/close events synchronously), so no CSS override is needed and the
    // close no longer gets stuck. Overlay menus/dialogs render in the root injector and are
    // unaffected — this scopes to what's in this template.
    { provide: MATERIAL_ANIMATIONS, useValue: { animationsDisabled: true } },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    AsyncPipe,
    FooterComponent,
    ToolbarComponent,
    SidenavComponent,
    UserMenuPanelComponent,
    ConsentManagementComponent,
    MatSidenav,
    MatSidenavContent,
    MatSidenavContainer,
  ],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  isProd!: boolean;
  envName!: string;
  fbLink!: string;
  twitterLink!: string;
  linkedinLink!: string;
  instagramLink!: string;
  version: any;
  g4PropertyId!: string;

  // Signal per lo stato di autenticazione
  authLoginState = signal<AuthLoginState | null>(null);
  // Observable originale (mantenuto per compatibilità con altri componenti)
  authLoginState$!: Observable<AuthLoginState>;

  user$!: Observable<UserDTO>;
  topic$!: Observable<Topic | undefined>;
  topics$!: Observable<Topic[]>;
  specializations$!: Observable<PanelItem[]>;
  subjects$!: Observable<PanelItem[]>;
  headerBannerContent$!: Observable<Content[]>;

  // User menu sidenav
  readonly userMenuSidenav = viewChild<MatSidenav>('userMenuSidenav');
  isUserMenuOpen = signal<boolean>(false);

  isBrowser: boolean = false;
  private toolbarResizeObserver: ResizeObserver | null = null;

  // Cache for pagehide handler
  private cachedAccessToken = '';
  private cachedSessionID = 0;
  private cachedCurrentPage: CurrentPage = { page: '' };
  private boundBeforeUnloadHandler = this.onBeforeUnload.bind(this);

  //private ngZone = inject(NgZone);
  constructor(
    private store: Store,
    private environmentService: EnvironmentService,
    private logService: LogService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private localStorageService: LocalStorageService,
    private envService: EnvironmentService,
    private translateService: TranslateService,
    private location: Location,
    private ga4Service: GA4ConfigService,
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private actRoute: ActivatedRoute,
    private destroyRef: DestroyRef,
    private clickService: ClickService
    //private appRef: ApplicationRef // Inject ApplicationRef
  ) {
    /*
    const ngZone = ɵglobal.Zone;
    const TaskTrackingZone = ngZone.current._parent?._properties?.TaskTrackingZone;

    if (!TaskTrackingZone) {
      return;
    }
      */
  }
  ngOnInit(): void {
    this.g4PropertyId = this.environmentService.ga4Property;

    this.isBrowser = isPlatformBrowser(this.platformId);


    // Debug ApplicationRef.isStable to track hydration stability
    // this.appRef.isStable.subscribe(stable => this.logService.info('ApplicationRef.isStable: ' + stable));

    /*
    // print all zone tasks to understand which tasks are running and are making the app unstable
    if (ɵglobal.Zone) {
      ɵglobal.Zone.current.fork({
        name: 'debug',
        onHasTask: (delegate, current, target, hasTask) => {
          console.log('Zone hasTask:', hasTask);
        }
      });

    }
    */

    this.isProd = this.environmentService.production;
    this.envName = this.environmentService.envName;
    this.version = this.environmentService.versions.app;
    this.fbLink = this.environmentService.fbLink;
    this.twitterLink = this.environmentService.twitterLink;
    this.linkedinLink = this.environmentService.linkedinLink;
    this.instagramLink = this.environmentService.instagramLink;

    this.logService.init(true, true, false, this.envService.logLevel);
    this.logService.info('logService started');


    this.store.dispatch(loadTemplateCt());
    this.store.dispatch(loadTopic());
    this.store.dispatch(dnLivreaConfig());

    if (isPlatformBrowser(this.platformId)) {
      this.logService.info('Startup - Dottnet Running in browser environment');

      // Scroll to top on route navigation (needed because .app-wrapper is the scroll container, not the window)
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        const wrapper = this.document.querySelector('.app-wrapper');
        if (wrapper) {
          wrapper.scrollTo(0, 0);
        }
      });

    } else {
      this.logService.info('Startup - Dottnet Running in SSR environment');
    }

    if (isPlatformBrowser(this.platformId)) {
      // Cache store values for pagehide handler
      this.store.select(selectGetAccessToken).pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(token => this.cachedAccessToken = token?.token ?? '');
      this.store.select(selectSessionID).pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(sessionID => this.cachedSessionID = sessionID);
      this.store.select(selectCurrentPage).pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(currentPage => this.cachedCurrentPage = currentPage);

      window.addEventListener('pagehide', this.boundBeforeUnloadHandler);

      this.ga4Service.configGlobalProperty(this.g4PropertyId); // Configuro la property globale
      this.translateService.use(this.envService.defaultLanguage);  //Setto il file di lingua da utilizzare
      this.document.documentElement.lang = this.envService.defaultLanguage;

      // Load header banner data globally (needed for toolbar on all pages)
      this.store.select(selectSessionID).pipe(
        filter((sessionId: number) => sessionId > 0),
        take(1),
        withLatestFrom(this.store.select(selectUserId))
      ).subscribe(([, userId]) => {
        this.store.dispatch(loadHomeHeaderBanner({
          userId,
          fromWhere: 0,
          howMany: 6
        }));
      });

      // this gets only the path, without domain. In this case it's just what we need
      const urlPath: string = this.location.path(true); // includes fragment
      const params: ParamMap = getQueryParametersFromURL(urlPath);

      // Capture UTMs now, before they're stripped from the URL below, so the
      // minisite/sponsor GA4 page_view (fired later, post-strip) can still carry
      // campaign attribution. The global property already gets them from its
      // pre-strip auto page_view.
      const utmQuery = UTM_PARAMS
        .map(k => {
          const v = params.get(k); // string | null — read once, narrow before encode
          return v ? `${k}=${encodeURIComponent(v)}` : '';
        })
        .filter(Boolean)
        .join('&');
      if (utmQuery) {
        this.ga4Service.setUtmQuery(utmQuery);
      }

      let oldSessionID = <number>this.localStorageService.getItem('SESSIONID');
      if (oldSessionID === null) {
        oldSessionID = 0;
      }

      const renewToken = <Token>this.localStorageService.getItem('RENEW');
      const currentLoginType = <LoginType>this.localStorageService.getItem('LOGINTYPE');

      let softToken: string = '';
      let confirmationMailToken: string = '';
      let renewFound: boolean = false;
      let softFound: boolean = false;

      const tkcm = params.get('tkcm');
      if (tkcm && tkcm.length > 0) {
        let referrer: string = '';
        if (isPlatformBrowser(this.platformId)) {
          referrer = this.document.referrer;
        }

        confirmationMailToken = tkcm;
        // Strip consumed params from callbackUrl so the activation email
        // links back to the clean page, not accumulating old tkcm tokens
        const cleanCallbackUrl = stripQueryParameters(urlPath, CONSUMED_QUERY_PARAMS);
        this.store.dispatch(
          activateUser({
            token: confirmationMailToken,
            callbackUrl: cleanCallbackUrl,
            params: params,
            oldSessionID: oldSessionID,
            url: urlPath,
            referrer: referrer,
          })
        );
      } else {
        const tksl = params.get('tksl');
        if (tksl && tksl.length > 0) {
          softToken = tksl;
          softFound = true;
        }
      }

      if (renewToken !== null && currentLoginType !== null) {
        this.logService.info('Startup -Found renew token in local storage: ', renewToken.token);
        this.logService.info('Startup -Found login type in local storage: ', currentLoginType);

        const scadenzaRenewToken = renewToken.expires;
        const adesso = Math.floor(Date.now() / 1000);
        // Any unexpired token is worth attempting to renew — the server decides
        // whether it's still acceptable. Deleting tokens with a few minutes of
        // life left silently demoted returning users to guest.
        if (scadenzaRenewToken > adesso) {
          renewFound = true;
        } else {
          this.logService.info(
            'Startup -Removing renew token because expired: ',
            renewToken.token
          );
          this.localStorageService.removeItem('RENEW');
          this.localStorageService.removeItem('LOGINTYPE');
        }
      }

      if (renewFound && softFound && currentLoginType === LoginType.HARDLOGIN) {
        this.store.dispatch(setLocalStorageAuth({ token: renewToken, loginType: currentLoginType }));
      }

      if (
        renewFound &&
        softFound &&
        (currentLoginType === LoginType.SOFTLOGIN || currentLoginType === LoginType.GUESTLOGIN)
      ) {
        this.store.dispatch(authSoftLogin({ token: softToken }));
      }

      if (renewFound && !softFound) {
        this.store.dispatch(setLocalStorageAuth({ token: renewToken, loginType: currentLoginType }));
      }

      if (!renewFound && softFound) {
        this.store.dispatch(authSoftLogin({ token: softToken }));
      }

      if (!renewFound && !softFound) {
        // new-login (2.10): niente token in localStorage NON significa "guest".
        // Il renew token vive nel cookie HttpOnly (illeggibile da JS), quindi si
        // tenta un renew ALLA CIECA: se il cookie c'è, l'utente di ritorno si
        // rilogga; un 401 → loginerror → authGuestLogin (fallback). Senza questo,
        // un utente già migrato al cookie verrebbe declassato a guest a ogni cold
        // boot. Vedi renew$/online$/loginerror (CU-30/31/32).
        this.logService.info(
          'Startup - No local credential. Attempting a blind renew (HttpOnly cookie).',
          undefined
        );
        this.store.dispatch(authRenewToken());
      }

      this.store.select(selectAuthLoginState).pipe(
        onlyWhenOnline(this.store),
        onlyWhenIdentityResolved(this.store),
        take(1),
        withLatestFrom(this.store.select(selectUser)),
        mergeMap(([authLoginState, userDTO]) => {
          // just path, without domain
          const url: string = this.location.path(true); // includes fragment
          const params: ParamMap = getQueryParametersFromURL(url);

          let oldSessionID = <number>this.localStorageService.getItem('SESSIONID');
          if (oldSessionID === null) {
            oldSessionID = 0;
          }

          this.logService.info(
            'Startup -Creating session. current login state: ',
            authLoginState.toString()
          );

          let referrer: string = '';
          if (isPlatformBrowser(this.platformId)) {
            referrer = this.document.referrer;
          }

          this.store.dispatch(
            createSession({
              params: params,
              oldSessionID: oldSessionID,
              url: url,
              referrer: referrer,
            })
          );

          // If the user is authenticated, anf if we have the right parameters, we can save privacy consent automatically
          // just 1 flag is mandatory, the others are optional
          this.logService.info(
            'Startup - User authenticated, checking for external consent parameters'
          );

          const tag = params.get('tag');
          const num = params.get('num');
          const flg1 = params.get('flg1');
          if (
            tag !== null &&
            tag.length > 0 &&
            num !== null &&
            num.length > 0 &&
            flg1 !== null &&
            flg1.length > 0
          ) {
            let externalConsentPayload: ExternalConsent = <ExternalConsent>{};

            this.logService.info('Startup - External consent parameters found, creating payload');

            externalConsentPayload.tagGlobale = parseInt(tag, 10);
            externalConsentPayload.numberId = parseInt(num, 10);
            externalConsentPayload.idAnagrafica = userDTO.idAnagrafica;
            externalConsentPayload.flag1 = evaluateBooleanFromString(flg1);

            const flg2 = params.get('flg2');
            if (flg2 && flg2.length > 0) {
              externalConsentPayload.flag2 = evaluateBooleanFromString(flg2);
            }

            const flg3 = params.get('flg3');
            if (flg3 && flg3.length > 0) {
              externalConsentPayload.flag3 = evaluateBooleanFromString(flg3);
            }

            const flg4 = params.get('flg4');
            if (flg4 && flg4.length > 0) {
              externalConsentPayload.flag4 = evaluateBooleanFromString(flg4);
            }

            this.store.dispatch(ExternalConsentActions.try({ externalConsent: externalConsentPayload }));
          }

          // Strip consumed query parameters from the browser URL
          // Wait for the router to complete initial navigation so Angular primitives are available.
          // If NavigationEnd already fired before this mergeMap ran (fast routing, slow auth),
          // router.navigated is true and we execute immediately via of(null) to avoid missing the event.
          const navigationReady$ = this.router.navigated
            ? of(null)
            : this.router.events.pipe(
              filter(event => event instanceof NavigationEnd),
              take(1),
            );

          navigationReady$.subscribe(() => {
            const cleanUrl = stripQueryParameters(this.router.url, CONSUMED_QUERY_PARAMS);
            if (cleanUrl !== this.router.url) {
              this.logService.info('Stripping consumed query params. Rewriting URL to: ', cleanUrl);
              // Use Location.replaceState (history.replaceState) rather than
              // router.navigateByUrl so no second ROUTER_NAVIGATED fires —
              // otherwise ClickEffects.savePageViews saves a duplicate page
              // view with the stripped URL, AND the dynamic-container route
              // re-mounts mid-bootstrap, racing the still-settling session
              // cookie (the documented cold-load auto-logout on /canale and
              // /minisito).
              this.location.replaceState(cleanUrl);
            }
          });

          return of(null);
        })).subscribe();

    } else {
      this.translateService.use(this.environmentService.defaultLanguage);
      this.logService.info('Startup - Dispatch GuestLogin auth for SSR');
      // new-login (1.10): loadUserGuest ora innesca authUserLoadedGuest tramite
      // loadUserGuestLoaded$ (user.effects), nell'ordine corretto: utente caricato
      // → poi LOADED. Rimosso il dispatch manuale a rovescio di authUserLoadedGuest.
      this.store.dispatch(loadUserGuest({}));
    }

    // Inizializzazione dell'Observable authLoginState$
    this.authLoginState$ = this.store.select(selectAuthLoginState);

    // Sottoscrizione all'Observable per aggiornare il signal
    this.authLoginState$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state) => {
      if (state) {
        this.authLoginState.set(state);
      }
    });

    this.user$ = this.store.select(selectUser);
    this.topic$ = this.store.select(selectCurrentTopic);
    this.topics$ = this.store.select(selectAllTopicList);
    this.subjects$ = this.store.select(selectCategorie).pipe(
      map(topics => topics.map(t => ({ id: t.topicID, label: t.topicDescription, permalink: t.permalink })))
    );
    this.specializations$ = this.store.select(selectSpecializzazioni).pipe(
      map(topics => topics.map(t => ({ id: t.topicID, label: t.topicDescription, permalink: t.permalink })))
    );
    this.headerBannerContent$ = this.store.select(selectWidgetHomeHeaderBannerContent);

    if (!this.isBrowser) {
      this.logService.info('Start rendering app server-side');
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      const toolbarEl = this.document.querySelector('dottnet-toolbar');
      if (toolbarEl) {
        this.toolbarResizeObserver = new ResizeObserver(entries => {
          for (const entry of entries) {
            const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
            this.document.documentElement.style.setProperty('--toolbar-height', `${height}px`);
            const appWrapper = this.document.querySelector('.app-wrapper') as HTMLElement | null;
            if (appWrapper) {
              this.document.documentElement.style.setProperty('--client-width', `${appWrapper.clientWidth}px`);
              this.document.documentElement.style.setProperty('--scrollbar-width', `${appWrapper.offsetWidth - appWrapper.clientWidth}px`);
            }
          }
        });
        this.toolbarResizeObserver.observe(toolbarEl);
      }
    }
  }

  ngOnDestroy(): void {
    this.toolbarResizeObserver?.disconnect();
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('pagehide', this.boundBeforeUnloadHandler);
    }
  }

  private onBeforeUnload(): void {
    const clickLog: ClickLog = {
      sessionID: this.cachedSessionID,
      clickLogOperationID: ClickOperation.AbbandonoPagina,
      content: JSON.stringify(this.cachedCurrentPage),
      message: 'Abbandono pagina',
      method: 'UNLOAD',
      outcome: false,
    };

    this.clickService.CreateClickLogOnUnload(clickLog);
  }

  onUserMenuClick(): void {
    this.userMenuSidenav()?.toggle();
    this.isUserMenuOpen.set(this.userMenuSidenav()?.opened ?? false);
  }

  onUserMenuClose(): void {
    this.userMenuSidenav()?.close();
    this.isUserMenuOpen.set(false);
  }
}


